// socketEventHandlers.js

import { useDispatch, useSelector } from 'react-redux';
import toast from 'react-hot-toast';

import {
  addMessage,
  incrementUnReadMessage,
  removeMessage,
  setDialogue,
  updateMessage,
  updateSession,
  selectChat,
  addDialogueUsersId,
} from '../redux/liveChat/slice';

import { setUser } from '../redux/users/slice';
import { updateUserBalance } from '../redux/auth/slice';
import {
  addStream,
  removeStream,
  updateStream,
} from '../redux/videoChat/slice';
import { addOnlineUser, removeOnlineUser } from '../redux/websocket/slice';
import { selectCurrentChat } from '../redux/liveChat/selectors';
import { selectIsManager, selectUser } from '../redux/auth/selectors';

import ChatNotification from '../components/customComponents/ChatNotification/ChatNotification';
import LetterNotification from '../components/customComponents/LetterNotification/LetterNotification';
import SignUpNotification from '../components/customComponents/SignUpNotification/SignUpNotification';

import { fetchUser } from '../api/usersApi';
import { fetchAgencyUserById } from '../api/agencyUsers';
import { useSounds } from './useSounds';
import { updateLiveChatSenders } from '../redux/liveChatSender/slice';

let typingId = null;

export const useSocketEventHandlers = () => {
  const dispatch = useDispatch();
  const currentUser = useSelector(selectUser);
  const currentChat = useSelector(selectCurrentChat);
  const isManager = useSelector(selectIsManager);
  const { playChatNotification, playReplyChatNotification } = useSounds();

  return {
    async sendMessage(message) {
      if (message.chatId === currentChat?._id) {
        if (message.senderId !== currentUser._id) {
          playReplyChatNotification();
        }
        dispatch(addMessage(message));
      } else {
        console.log(message);

        let user;
        const isSupport =
          message.senderType === 'agencyUserSupport' ||
          message.senderType === 'support';

        if (isSupport) {
          user = await fetchAgencyUserById(message.senderId);
        } else {
          user = await fetchUser(message.senderId);
          dispatch(setUser(user));
        }

        const shouldNotify =
          !isManager &&
          // message.senderType !== 'paidUserSupport' &&
          message.senderId !== currentUser._id;

        if (shouldNotify) {
          playChatNotification();
          toast(
            t => <ChatNotification letter={message} id={t.id} user={user} />,
            {
              duration: 30000,
              position: 'top-right',
            },
          );
        }
        if (message.senderId !== currentUser._id) {
          dispatch(incrementUnReadMessage(message.chatId));
        }
      }

      dispatch(updateSession({ chatId: message.chatId, lastMessage: message }));
      dispatch(setDialogue({ _id: message.chatId, lastMessage: message }));
      dispatch(addDialogueUsersId(message.users));
    },

    async newLetter(message) {
      const user = await fetchUser(message.senderId);
      dispatch(setUser(user));
      playChatNotification();

      toast(
        t => <LetterNotification letter={message} id={t.id} user={user} />,
        {
          duration: 30000,
          position: 'top-right',
        },
      );
    },

    loginUser(data) {
      playChatNotification();
      toast(t => <SignUpNotification user={data} id={t.id} />, {
        duration: 30000,
        position: 'top-right',
      });
    },

    updateBalance({ balance }) {
      dispatch(updateUserBalance(balance));
    },

    updateMessage(message) {
      dispatch(updateMessage(message));
    },

    removeMessage({ messageId }) {
      dispatch(removeMessage(messageId));
    },

    userTyping({ chatId }) {
      if (!currentChat?._id || chatId !== currentChat?._id) return;

      dispatch(selectChat({ ...currentChat, userTyping: true }));

      if (typingId) clearTimeout(typingId);
      typingId = setTimeout(() => {
        dispatch(selectChat({ ...currentChat, userTyping: false }));
        typingId = null;
      }, 3000);
    },

    createStream(stream) {
      dispatch(addStream(stream));
    },

    updateStream(data) {
      dispatch(updateStream(data));
    },

    removeStream({ meetingId }) {
      console.log('removeStream!');

      dispatch(removeStream(meetingId));
    },

    addOnlineUser(data) {
      console.log('addOnline user', data);

      dispatch(addOnlineUser(data.id));
    },

    removeOnlineUser(data) {
      console.log('removeOnline user', data);
      dispatch(removeOnlineUser(data.id));
    },

    changeBotStatus({ status }) {
      if (status === 'ENABLED') {
        toast.success('Live Chat Bot is sending messages to all Online Users');
      } else {
        toast.error('Live Chat Bot has been stopped');
      }
      dispatch(updateLiveChatSenders({ status }));
    },
  };
};
